import { useIntl } from 'react-intl'
import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import { formatOccupantsShort, sumUpOccupancies } from 'lib/offer/occupancyUtils'

import { formatGuestsDisplay } from 'lib/string/stringUtils'
import React, { useMemo } from 'react'
import moment from 'moment'
import useDefaultAirport from 'hooks/useDefaultAirport'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import TextButton from 'LuxKit/Button/TextButton'
import config from 'constants/config'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { useOfferPriceWithMember } from 'hooks/Offers/useOfferPrice'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'
import { EmptyArray, unique } from 'lib/array/arrayUtils'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import offerPageURL from 'lib/offer/offerPageURL'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsMultiRoomEnabled } from 'selectors/featuresSelectors'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { FlightsClassTypes } from 'constants/flight'
import useIsSearchPage from 'hooks/Navigation/useIsSearchPage'
import { useMaldivesBundleContext } from 'components/Pages/HotelSearchPage/MaldivesBundleContext'
import { getCharterFareClassPolicy } from 'lib/charterFlights/charterOfferFareClassRestrictions'

interface MappedProps {
  canViewLuxPlusBenefits: boolean
}

interface Props extends MappedProps {
  offer: App.Offer | App.OfferSummary
  filters?: App.OfferListFilters
  rooms?: Array<App.Occupants>
  pkg: App.Package
  pricing?: App.OfferAvailableRate
  hasDates?: boolean
  offerUrl?: string
}

function SearchTileFlightOnlyPricing(props: Props) {
  const {
    offer,
    pkg,
    pricing,
    hasDates,
    rooms = EmptyArray,
    offerUrl,
    canViewLuxPlusBenefits,
    filters,
  }: Props = props

  const intl = useIntl()
  // Use bundle filters if this offer is a charter offer, otherwise use original filters
  const { bundleFilters, isBundleMode } = useMaldivesBundleContext()
  const isMultiRoomEnable = useAppSelector(state => getIsMultiRoomEnabled(state, offer))
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))
  const isSearchPage = useIsSearchPage()
  const charterFareClassPolicy = useMemo(() => {
    return getCharterFareClassPolicy({
      campaignTags: offer.campaignTags,
      occupancies: rooms,
    })
  }, [offer.campaignTags, rooms])
  const effectiveFareClass = charterFareClassPolicy.defaultFareClass ?? FlightsClassTypes.ECONOMY

  // Use bundle filters if this offer is a charter bundle offer and bundle mode is enabled
  const shouldUseBundleFilters = !!(isBundleMode && bundleFilters && charterBundleRoute)

  // Use bundle filters when in bundle mode, otherwise use original filters
  const bundleSearchPrices = useDirectSearchPrices({ filters: bundleFilters ?? {}, offerId: offer.id })
  const searchPrices = useDirectSearchPrices({ filters: filters ?? {}, offerId: offer.id })

  // Use bundle pricing only when available to avoid mixing search durations/prices
  const directSearchPrices = shouldUseBundleFilters ? bundleSearchPrices : searchPrices
  const shouldFetchCharterFlightPrice = !!(isSearchPage && charterBundleRoute && bundleFilters?.checkIn && bundleFilters?.checkOut)

  // Use useCharterFlightPrice to get flight price for Maldives charter bundle
  const [charterFlightPrice] = useCharterFlightPrice({
    charterBundleRoute,
    checkInDate: bundleFilters?.checkIn,
    checkOutDate: bundleFilters?.checkOut,
    occupancies: rooms ?? [],
    fareClass: effectiveFareClass,
    disabled: !shouldFetchCharterFlightPrice,
  })

  // Fallback to offer.flightPrices if charter flight price is not available
  const defaultAirport = useDefaultAirport()
  const fallbackFlightPrice = (offer.flightPrices[defaultAirport?.code ?? ''] || 0)
  const flightPrice = (charterBundleRoute && charterFlightPrice > 0) ? charterFlightPrice : fallbackFlightPrice
  // Calculate totalGuests from rooms
  const totalGuests = useMemo(() => {
    if (rooms.length === 0) return undefined
    return sumUpOccupancies(rooms)
  }, [rooms])

  const guestsDisplayText = useMemo(() => {
    if (!isBundleMode || !totalGuests) return undefined
    return formatGuestsDisplay(totalGuests, intl)
  }, [isBundleMode, totalGuests, intl])

  const shouldShowPerPersonPrice = !!(isSearchPage && charterBundleRoute && totalGuests && totalGuests > 0)
  const perPersonDivisor = shouldShowPerPersonPrice ? totalGuests : 1

  const bundleDuration = useMemo(() => {
    if (!bundleFilters?.checkIn || !bundleFilters?.checkOut) return undefined
    return moment(bundleFilters.checkOut).diff(bundleFilters.checkIn, 'days')
  }, [bundleFilters?.checkIn, bundleFilters?.checkOut])

  const bundlePackage = useMemo(() => {
    if (!shouldUseBundleFilters || !bundleDuration || !offer.packages) return undefined
    return offer.packages.find(currentPackage => currentPackage.duration === bundleDuration)
  }, [shouldUseBundleFilters, bundleDuration, offer.packages])

  const resolvedPackage = bundlePackage ?? pkg
  const resolvedPricing = bundlePackage ? undefined : pricing

  const priceMultiplier = rooms?.length || 1
  const extraGuestSurcharges = useMemo(() => getSumExtraGuestsSurcharge(rooms || [], offer, resolvedPackage), [resolvedPackage, rooms, offer])

  const {
    price = directSearchPrices?.lowestPrice ?? 0,
    memberPrice = directSearchPrices?.lowestMemberPrice ?? 0,
    taxesAndFees = 0,
    propertyFees = 0,
  } = resolvedPricing ?? resolvedPackage

  const showPriceDetails = taxesAndFees > 0 || propertyFees > 0 || config.agentHub.isEnabled
  let hotelPrice = price * priceMultiplier + extraGuestSurcharges + flightPrice
  const showMemberPrice = memberPrice > 0
  const rateOrPkgMemberPrice = resolvedPricing?.memberPriceWithSurcharge ?? resolvedPackage.memberPrice
  const hotelMemberPrice = showMemberPrice ? rateOrPkgMemberPrice * priceMultiplier + extraGuestSurcharges + flightPrice : 0
  let totalPrice = hotelPrice + propertyFees
  let totalMemberPrice = showMemberPrice ? hotelMemberPrice + propertyFees : 0
  const pkgValueOrMemberValue = canViewLuxPlusBenefits ? resolvedPackage.memberValue : resolvedPackage.value
  let totalValue = pkgValueOrMemberValue * priceMultiplier + extraGuestSurcharges + flightPrice
  let shouldShowValue = resolvedPackage.shouldDisplayValue && pkgValueOrMemberValue > 0 && totalValue >= (showMemberPrice ? totalMemberPrice : totalPrice)

  if (directSearchPrices) {
    // search does not know anything about flight prices, so we just add them in to our pricing
    hotelPrice = (directSearchPrices.lowestPrice ?? 0) + flightPrice
    totalPrice = hotelPrice // we already include property fees in the direct search prices
    totalMemberPrice = showMemberPrice ? (directSearchPrices.lowestMemberPrice ?? 0) + flightPrice : 0
    totalValue = (directSearchPrices.lowestPriceValue ?? 0) + flightPrice
    shouldShowValue = resolvedPackage.shouldDisplayValue && pkgValueOrMemberValue > 0 && totalValue >= (showMemberPrice ? totalMemberPrice : totalPrice)
  }

  const valueOffPercent = getValueOffPercent(totalValue, showMemberPrice ? hotelMemberPrice : hotelPrice)

  const displayTotalPrice = perPersonDivisor > 1 ? totalPrice / perPersonDivisor : totalPrice
  const displayTotalMemberPrice = perPersonDivisor > 1 ? totalMemberPrice / perPersonDivisor : totalMemberPrice
  const priceSaleUnit = shouldShowPerPersonPrice ? 'person' : 'total'

  // https://aussiecommerce.atlassian.net/browse/CONLT-863
  // Only show discount pill for forced bundles
  const shouldShowRate = !offer.forceBundleId && offer.bundledWithFlightsOnly && valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const {
    total: displayPrice,
    memberTotal: displayMemberPrice,
    saleUnit: displaySaleUnit,
  } = useOfferPriceWithMember({
    value: undefined,
    saleUnit: priceSaleUnit,
    duration: resolvedPackage.duration,
    total: displayTotalPrice,
    memberTotal: displayTotalMemberPrice,
  })

  const priceCaption = useMemo(() => {
    const roomCount = rooms.length
    const showRooms = isMultiRoomEnable && roomCount > 1
    const hasFlightPrice = flightPrice > 0
    const occupants = formatOccupantsShort(rooms)
    const roomsText = showRooms ? intl.formatMessage(
      {
        id: 'o4k5U80H3v',
        defaultMessage: '{count, plural, one {# room} other {# rooms}}',
        description: 'Room count for flight search tile pricing',
      },
      { count: roomCount },
    ) : ''

    const formatNightsCaption = (nightCount: number) => {
      if (hasFlightPrice) {
        if (showRooms) {
          return intl.formatMessage(
            {
              id: '5NZ5Lj1HH5',
              defaultMessage: '{nights, plural, one {# night} other {# nights}}, {roomsText} & flights, {occupants}',
              description: 'Duration with rooms, flights, and occupants for flight search tile pricing',
            },
            { nights: nightCount, roomsText, occupants },
          )
        }
        return intl.formatMessage(
          {
            id: 'q0nAkrKOcM',
            defaultMessage: '{nights, plural, one {# night} other {# nights}} & flights, {occupants}',
            description: 'Duration with flights and occupants for flight search tile pricing',
          },
          { nights: nightCount, occupants },
        )
      }
      if (showRooms) {
        return intl.formatMessage(
          {
            id: 'X9wL+rfyDz',
            defaultMessage: '{nights, plural, one {# night} other {# nights}}, {roomsText}, hotel only, {occupants}',
            description: 'Duration with rooms, hotel only, and occupants for flight search tile pricing',
          },
          { nights: nightCount, roomsText, occupants },
        )
      }
      return intl.formatMessage(
        {
          id: '5REgnMfJ3x',
          defaultMessage: '{nights, plural, one {# night} other {# nights}}, hotel only, {occupants}',
          description: 'Duration with hotel only and occupants for flight search tile pricing',
        },
        { nights: nightCount, occupants },
      )
    }

    if (shouldUseBundleFilters && bundleDuration) {
      return formatNightsCaption(bundleDuration)
    }
    if (directSearchPrices?.duration) {
      return formatNightsCaption(directSearchPrices.duration)
    }
    if (hasDates) {
      if (showRooms) {
        return intl.formatMessage(
          {
            id: '+dULCQg4gV',
            defaultMessage: '{nights, plural, one {# night} other {# nights}}, {roomsText} & flights, {occupants}',
            description: 'Duration with rooms, flights, and occupants for flight search tile with dates',
          },
          { nights: resolvedPackage.duration, roomsText, occupants },
        )
      }
      return intl.formatMessage(
        {
          id: '67KGlYckv5',
          defaultMessage: '{nights, plural, one {# night} other {# nights}} & flights, {occupants}',
          description: 'Duration with flights and occupants for flight search tile with dates',
        },
        { nights: resolvedPackage.duration, occupants },
      )
    }
    if (offer.tileDurationLabel) {
      if (showRooms) {
        return intl.formatMessage(
          {
            id: 'NLaj8defMk',
            defaultMessage: '{label}, {roomsText} & flights from',
            description: 'Duration label with rooms and flights for flight search tile pricing',
          },
          { label: offer.tileDurationLabel.toLocaleLowerCase(), roomsText },
        )
      }
      return intl.formatMessage(
        {
          id: '9n0ThZv69o',
          defaultMessage: '{label} & flights from',
          description: 'Duration label with flights for flight search tile pricing',
        },
        { label: offer.tileDurationLabel.toLocaleLowerCase() },
      )
    }
    return intl.formatMessage({
      id: 'dDu3dw/T/j',
      defaultMessage: 'Hotel & flights from',
      description: 'Fallback price caption for flight search tile pricing',
    })
  }, [rooms, isMultiRoomEnable, shouldUseBundleFilters, bundleDuration, directSearchPrices?.duration, hasDates, resolvedPackage.duration, offer.tileDurationLabel, flightPrice, intl])

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  return <div>
    <PriceRowPriceCaption>
      {priceCaption}
    </PriceRowPriceCaption>
    <LuxPlusPriceStack
      price={displayPrice}
      memberPrice={displayMemberPrice}
      size="L"
      saleUnit={displaySaleUnit}
      testid="search-price"
      offerConfig={offer.luxPlus}
    />
    <PriceRowTaxesAndFees />
    {shouldShowValue && <PriceRowValueDiscountWithCaption
      data-testid="value-box"
      size="M"
      originalValue={totalValue}
      discountPercentage={shouldShowRate ? valueOffPercent : undefined}
    />}
    {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}
    <PriceRowAgentHubCommission
      productType={offer.productType}
      offerId={offer.id}
      vendorName={vendorName}
      offerLocations={offerLocations}
      packageId={pkg.id}
    />
    {showPriceDetails && <OfferPriceDetailsRow
      trigger="price-row"
      triggerSize="M"
      offer={offer}
      duration={resolvedPackage.duration}
      propertyFees={propertyFees}
      hotelPrice={hotelPrice}
      rooms={rooms}
      dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
      hotelMemberPrice={hotelMemberPrice}
      showMemberPrice={showMemberPrice}
      context="search"
      cta={
        <TextButton kind="primary" fit="flex" to={offerUrl ?? offerPageURL(offer)}>
          {intl.formatMessage({
            id: 'HXYYtZKv2m',
            defaultMessage: 'View Offer',
            description: 'CTA button in flight pricing details to view the full offer',
          })}
        </TextButton>
      }
    />}
  </div>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(state),
})

export default connect(mapStateToProps)(SearchTileFlightOnlyPricing)
